// 게임 루프 관리 클래스
class GameLoop {
  constructor(game) {
    this.game = game;
    this.isRunning = false;
    this.isPaused = false; // 일시정지 상태 (루프는 계속 실행하되 업데이트만 제한)
    this.animationId = null;
  }

  // 게임 루프 시작
  start() {
    if (this.isRunning) {
      Utils.log("게임 루프가 이미 실행 중입니다.", "warn");
      return;
    }

    this.isRunning = true;
    Utils.log("게임 루프 시작");
    this.loop();
  }

  // 게임 루프 중지
  stop() {
    if (!this.isRunning) {
      Utils.log("게임 루프가 실행 중이 아닙니다.", "warn");
      return;
    }

    this.isRunning = false;
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    Utils.log("게임 루프 중지");
  }

  // 게임 일시정지 (루프는 계속 실행, 업데이트만 제한)
  pause() {
    this.isPaused = true;
    Utils.log("게임 일시정지 (카메라는 계속 업데이트)");
  }

  // 게임 재개
  resume() {
    this.isPaused = false;
    Utils.log("게임 재개");
  }

  // 메인 게임 루프
  loop() {
    if (!this.isRunning) return;

    this.animationId = requestAnimationFrame(() => this.loop());

    const delta = this.game.clock.getDelta();
    const currentTime = Date.now() / 1000;

    // 게임 업데이트
    this.updateGame(delta, currentTime);

    // 렌더링
    this.render();
  }

  // 게임 상태 업데이트
  updateGame(delta, currentTime) {
    // 게임 종료 상태일 때는 애니메이션만 업데이트하고 나머지는 중단
    if (this.game.isGameOverState()) {
      // 플레이어 애니메이션 믹서만 업데이트 (죽음 애니메이션 재생)
      if (this.game.player && this.game.player.mixer) {
        this.game.player.mixer.update(delta);
      }

      // TODO : 동료들의 애니메이션 믹서 업데이트 (앉기 애니메이션 재생 앉기 애니메이션은 Companion 클래스)
      if (this.game.companionManager) {
        this.game.companionManager.companions.forEach((companion) => {
          if (companion.mixer) {
            companion.mixer.update(delta);
          }
        });
      }

      // 적들의 애니메이션 믹서 업데이트 (victory 애니메이션 재생)
      if (this.game.enemySpawnManager) {
        this.game.enemySpawnManager.enemies.forEach((enemy) => {
          if (enemy.mixer) {
            enemy.mixer.update(delta);
          }
        });
      }

      return;
    }

    // 일시정지 상태일 때는 카메라만 업데이트 (레벨업 카메라 회전 효과)
    if (this.isPaused) {
      this.updateCamera(delta);

      // 한국어 주석: 일시정지 중에도 수집된 아이템 정리는 실행 (애니메이션이 독립적으로 실행되므로)
      if (this.game.itemManager) {
        this.game.itemManager.cleanupCollectedItems();
      }

      return;
    }

    // 플레이어 업데이트
    this.updatePlayer(delta, currentTime);

    // 적 스폰 시스템 업데이트
    this.updateEnemySpawnSystem(delta);

    // 콜리션 시스템 업데이트
    this.updateCollisionSystem();

    // 아이템 시스템 업데이트
    this.updateItems(delta);

    // 동료 시스템 업데이트
    this.updateCompanions(delta);

    // 카메라 업데이트
    this.updateCamera(delta);

    // 무한 스크롤 업데이트
    this.updateInfiniteWorld();

    // UI 업데이트
    this.updateUI();
  }

  // 플레이어 업데이트
  updatePlayer(delta, currentTime) {
    if (!this.game.player || !this.game.player.isModelLoaded()) return;

    this.game.player.update(
      delta,
      this.game.inputHandler,
      currentTime,
      this.game.camera
    );
  }

  // 새로운 적 스폰 시스템 업데이트
  updateEnemySpawnSystem(delta) {
    // 적 스폰 매니저 업데이트
    if (this.game.enemySpawnManager) {
      this.game.enemySpawnManager.update(delta);
    }

    // 스폰 스케줄러 업데이트
    if (this.game.spawnScheduler) {
      this.game.spawnScheduler.update(delta);
    }
  }

  // 콜리션 시스템 업데이트
  updateCollisionSystem() {
    if (
      !this.game.collisionManager ||
      !this.game.player ||
      !this.game.enemySpawnManager ||
      !this.game.companionManager
    ) {
      return;
    }

    // 콜리션 매니저 업데이트 (플레이어, 적들, 동료들 간의 충돌 검사 및 해결)
    this.game.collisionManager.update(
      this.game.player,
      this.game.enemySpawnManager.enemies,
      this.game.companionManager.companions
    );
  }

  // 아이템들 업데이트
  updateItems(delta) {
    if (!this.game.itemManager) return;

    // 아이템 매니저 업데이트 (생성, 충돌 검사, 업데이트 등)
    this.game.itemManager.update(delta);
  }

  // 동료들 업데이트
  updateCompanions(delta) {
    if (!this.game.companionManager || !this.game.enemySpawnManager) return;

    // 동료 매니저 업데이트 (AI 동작, 공격, 이동 등)
    this.game.companionManager.update(
      delta,
      this.game.enemySpawnManager.enemies,
      this.game.camera // 카메라 전달하여 체력바가 항상 카메라를 향하도록 함
    );
  }

  // 카메라 업데이트
  updateCamera(delta) {
    if (this.game.cameraController) {
      this.game.cameraController.update(delta);
    }
  }

  // 무한 스크롤 업데이트
  updateInfiniteWorld() {
    if (
      !this.game.sceneManager ||
      !this.game.player ||
      !this.game.player.isModelLoaded()
    ) {
      return;
    }

    this.game.sceneManager.updateInfiniteWorld(
      this.game.player.getPosition(),
      this.game.camera
    );

    // 환경 오브젝트 업데이트 후 플레이어와 동료들에게 새로운 참조 제공
    const environmentObjects = this.game.sceneManager.getEnvironmentObjects();
    this.game.player.setEnvironmentObjects(environmentObjects);

    // 동료들에게도 환경 오브젝트 설정
    if (this.game.companionManager) {
      this.game.companionManager.setEnvironmentObjects(environmentObjects);
    }
  }

  // 렌더링
  render() {
    if (this.game.renderer && this.game.scene && this.game.camera) {
      this.game.renderer.render(this.game.scene, this.game.camera);
    }
  }

  // UI 업데이트
  updateUI() {
    if (!this.game.uiManager || !this.game.player) return;

    // 플레이어 정보 통합 업데이트 (포인트, 레벨, 해골 카운트 등)
    this.game.uiManager.updatePlayerInfo(this.game.player);

    // 게임 시간 업데이트
    this.game.uiManager.updateGameTime();
  }

  // 게임 루프 상태 확인
  getStatus() {
    return {
      isRunning: this.isRunning,
      isPaused: this.isPaused,
      animationId: this.animationId,
    };
  }
}

// webpack 빌드를 위해 default export 추가
export default GameLoop;
